import { useTranslation } from "react-i18next";

import CloseRounded from "@mui/icons-material/CloseRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { formatDateTime } from "../format/formatDate";
import { DocumentPreview } from "./DocumentPreview";

interface Props {
  blob: Blob | null;
  isLoading?: boolean;
  fileName: string;
  uploadedAt?: Date;
  onClose: () => void;
}

export function DocumentPreviewDialog({ blob, isLoading = false, fileName, uploadedAt, onClose }: Props) {
  const { t } = useTranslation();

  const handleOpenInNewTab = () => {
    if (blob) {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle component="div" sx={{ p: "12px 16px" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{fileName}</Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            {uploadedAt !== undefined && (
              <Typography variant="body2" color="text.primary">
                {formatDateTime(uploadedAt)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={handleOpenInNewTab}
              disabled={isLoading}
              aria-label={t("documentPreview.openInNewTab")}
            >
              <OpenInNewRounded fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label={t("documentPreview.close")}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: "80vh" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          blob && <DocumentPreview blob={blob} fileName={fileName} />
        )}
      </DialogContent>
    </Dialog>
  );
}
